import {observer} from 'mobx-react-lite'
import React, {useEffect, useMemo} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {DataObjectId, PermissionRoleKey} from 'yougile-model'
import {Tracking, TrackingEvent} from '@common/tracking'
import {AvatarBase} from '@components/common/base/avatar'
import {Button} from '@components/common/base/buttons'
import {TriCheckboxNew} from '@components/common/base/checkbox-new'
import {CheckboxNew} from '@components/common/base/checkbox-new/checkbox-new'
import {
  OptionsItem,
  OptionsItemCheckbox
} from '@components/common/base/dropdown-button-menu/types'
import {Popup} from '@components/common/base/popup'
import {SelectWithDropdown} from '@components/common/base/select-with-dropdown'
import {SelectWithCheckboxes} from '@components/common/base/select-with-dropdown/select-with-checkboxes'
import {getValueByKey} from '@components/common/base/select-with-dropdown/utils'
import {TableHeadCell} from '@components/common/base/table'
import {TableCell} from '@components/common/base/table/table-cell'
import {
  PendingRegistration,
  pendingRegistrationToUserProxy
} from '@components/dashboard/user-fields'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'
import {pendingCompaniesAdminStore} from './pending-companies-admin-store'

export const RegistrationsPopup = observer(() => {
  useEffect(() => {
    void Tracking.send(
      TrackingEvent.companyLinkOpeningOfTheRegistrationConfirmationPopup
    )
  }, [])

  return (
    <Popup
      title={Lang.msg.users.registrationsPopupTitle}
      close={() => {
        pendingCompaniesAdminStore.setRegistrationPopupOpen(false)
        void Tracking.send(
          TrackingEvent.companyLinkClosingOfTheRegistrationConfirmationPopup
        )
      }}
      width={800}
      noScroll={true}
      autoHeight
    >
      <div className="mx-auto flex-1 flex flex-col h-[352px] my-8">
        <TableHeader />

        <div className="flex-1 flex flex-col overflow-auto min-h-[320px] max-h-[320px] h-[320px]">
          {pendingCompaniesAdminStore.pendingRegistrationsSince.map(item => (
            <TableRow key={item.userId} item={item} />
          ))}
        </div>
      </div>
      <div className="py-24 flex-none flex items-center justify-end gap-12">
        <Button
          color="error"
          size="medium"
          onClick={() => {
            void pendingCompaniesAdminStore.rejectSelectedRegistrations()
            void Tracking.send(
              TrackingEvent.companyLinkRejectUserRegistrationViaPopup
            )
          }}
          disabled={
            !pendingCompaniesAdminStore.state.selectedRegistrations.size
          }
        >
          {Lang.msg.users.rejectBtn}
        </Button>
        <Button
          size="medium"
          onClick={() => {
            void pendingCompaniesAdminStore.acceptSelectedRegistrations()
            void Tracking.send(
              TrackingEvent.companyLinkConfirmUserRegistrationViaPopup
            )
          }}
          disabled={
            !pendingCompaniesAdminStore.state.selectedRegistrations.size
          }
        >
          {Lang.msg.users.acceptBtn}
        </Button>
      </div>
    </Popup>
  )
})

const HeaderCell = tw(TableHeadCell)`
  cursor-default
  flex-col
  !py-8
  !px-12
  !items-start
`

const TableHeader = observer(() => {
  return (
    <div className="w-full flex items-center h-32">
      <HeaderCell
        minWidth={'40px'}
        maxWidth={'40px'}
        className="justify-center"
      >
        <TriCheckboxNew
          value={pendingCompaniesAdminStore.allSelected}
          onChange={pendingCompaniesAdminStore.toggleSelectAll}
        />
      </HeaderCell>
      <HeaderCell
        minWidth={'240px'}
        maxWidth={'240px'}
        className="justify-start"
      >
        Имя
      </HeaderCell>
      <HeaderCell
        minWidth={'200px'}
        maxWidth={'200px'}
        className="justify-start"
      >
        Электронная почта
      </HeaderCell>
      <HeaderCell minWidth={'140px'} maxWidth={'140px'}>
        Добавить в проект
      </HeaderCell>
      <HeaderCell minWidth={'132px'} maxWidth={'132px'} className="!items-end">
        Роль
      </HeaderCell>
    </div>
  )
})

const RowCell = tw(TableCell)`
  cursor-default
  py-8
  px-12
  items-center
`

const TableRow = observer(({item}: {item: PendingRegistration}) => {
  const user = useMemo(() => pendingRegistrationToUserProxy(item), [item])

  const projects = Project.listProjects(
    globals.getCompanyId(),
    'not-archive',
    true
  ).sort(Project.projectOrderComparer)
  const projectOptions: OptionsItemCheckbox[] = projects.map(x => ({
    selected: item.projectIds?.includes(x.id) ?? false,
    text: x.title,
    key: x.id,
    id: x.id
  }))

  const roleOptions: OptionsItem<PermissionRoleKey>[] = [
    {key: 'observer', text: Lang.msg.permission.observerRole},
    {key: 'worker', text: Lang.msg.permission.workerRole},
    {key: 'admin', text: Lang.msg.permission.adminRole}
  ]

  return (
    <div className="w-full flex items-center h-32 !text-12">
      <RowCell className="justify-center" minWidth={'40px'} maxWidth={'40px'}>
        <CheckboxNew
          value={pendingCompaniesAdminStore.state.selectedRegistrations.has(
            item.userId
          )}
          onChange={() => pendingCompaniesAdminStore.toggleSelect(item.userId)}
        />
      </RowCell>
      <RowCell
        className="justify-start gap-8"
        minWidth={'240px'}
        maxWidth={'240px'}
      >
        <AvatarBase user={user} size={16} hideStatus hideOutline hideHint />
        {item.username}
      </RowCell>
      <RowCell minWidth={'200px'} maxWidth={'200px'} className="justify-start">
        {item.email}
      </RowCell>
      <RowCell minWidth={'140px'} maxWidth={'140px'} className="justify-start">
        {!!projects.length && (
          <SelectWithCheckboxes
            size="32"
            options={projectOptions}
            onChange={xs =>
              pendingCompaniesAdminStore.setProjectsForRegistration(
                item.userId,
                xs.filter(x => x.selected).map(x => x.key as DataObjectId)
              )
            }
            link
            className="!w-full !max-w-full !text-12"
            innerClassName="w-[244px]"
            placement="bottom-end"
            value={xs => {
              if (xs.every(x => x.selected)) {
                return Lang.msg.all
              } else if (xs.every(x => !x.selected)) {
                return Lang.msg.users.noProjectsSelected
              } else {
                return xs
                  .filter(x => x.selected)
                  .map(x => x.text)
                  .join()
              }
            }}
          />
        )}
      </RowCell>
      <RowCell minWidth={'132px'} maxWidth={'132px'} className="justify-end">
        {!!item.projectIds?.length && (
          <SelectWithDropdown<PermissionRoleKey>
            link
            className="!w-fit !max-w-full !text-12"
            innerClassName="w-180"
            placement="bottom-end"
            value={getValueByKey(roleOptions, item.role ?? 'worker')}
            options={roleOptions}
            onChange={x =>
              pendingCompaniesAdminStore.setRoleForRegistration(
                item.userId,
                x.key
              )
            }
          />
        )}
      </RowCell>
    </div>
  )
})
